/**
 * ============================================================================
 * 🔌 useConnectionStatus — Server-Verbindungsstatus (Ping/Latenz)
 * ============================================================================
 * 
 * Zeigt den Verbindungsstatus zum Server an:
 * - 🟢 Grün: Verbunden (Latenz < 1000ms)
 * - 🟠 Orange: Instabil (Latenz > 1000ms oder schwankend)
 * - 🔴 Rot: Keine Verbindung
 * 
 * Prüft alle 15 Sekunden via /health Endpoint.
 * ============================================================================
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { SERVER_URL } from '../config/serverConfig';

// Schwellwerte
const LATENCY_GOOD = 1000;     // < 1s = grün
const LATENCY_WARN = 3000;     // < 3s = orange, darüber auch orange
const CHECK_INTERVAL = 15000;  // Alle 15 Sekunden prüfen
const HISTORY_SIZE = 5;        // Letzte 5 Messungen für Schwankungs-Erkennung

/**
 * @returns {{ status: 'connected'|'unstable'|'disconnected', latency: number|null, label: string }}
 */
export default function useConnectionStatus() {
  const [status, setStatus] = useState('connected'); // Optimistisch starten
  const [latency, setLatency] = useState(null);
  const latencyHistory = useRef([]);
  const mountedRef = useRef(true);

  const checkConnection = useCallback(async () => {
    if (!mountedRef.current) return;

    const start = performance.now();
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000); // 5s Timeout

      const response = await fetch(`${SERVER_URL}/health`, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });
      clearTimeout(timeout);

      if (!mountedRef.current) return;

      const elapsed = Math.round(performance.now() - start);
      setLatency(elapsed);

      // Historie aktualisieren
      latencyHistory.current.push(elapsed);
      if (latencyHistory.current.length > HISTORY_SIZE) {
        latencyHistory.current.shift();
      }

      if (!response.ok) {
        setStatus('unstable');
        return;
      }

      // Schwankung prüfen: Standardabweichung der letzten Messungen
      const history = latencyHistory.current;
      if (history.length >= 3) {
        const avg = history.reduce((a, b) => a + b, 0) / history.length;
        const variance = history.reduce((sum, v) => sum + Math.pow(v - avg, 2), 0) / history.length;
        const stdDev = Math.sqrt(variance);

        // Hohe Schwankung (> 50% des Durchschnitts) = instabil
        if (stdDev > avg * 0.5 && avg > 500) {
          setStatus('unstable');
          return;
        }
      }

      // Latenz-basierte Bewertung
      if (elapsed < LATENCY_GOOD) {
        setStatus('connected');
      } else if (elapsed < LATENCY_WARN) {
        setStatus('unstable');
      } else {
        setStatus('unstable');
      }

    } catch (err) {
      if (!mountedRef.current) return;
      setLatency(null);
      latencyHistory.current = [];
      setStatus('disconnected');
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    let interval = null;

    const startPolling = () => {
      if (!interval) {
        interval = setInterval(checkConnection, CHECK_INTERVAL);
      }
    };
    const stopPolling = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };

    // Visibility API: Polling stoppen wenn Tab inaktiv (spart Traffic + Akku)
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        checkConnection(); // Sofort prüfen bei Rückkehr
        startPolling();
      }
    };

    // Erster Check nach 2s (nicht sofort, damit App erst laden kann)
    const initialTimer = setTimeout(() => {
      checkConnection();
      startPolling();
    }, 2000);

    // Online/Offline Events
    const handleOnline = () => {
      setTimeout(checkConnection, 500);
    };
    const handleOffline = () => {
      setStatus('disconnected');
      setLatency(null);
      latencyHistory.current = [];
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      mountedRef.current = false;
      clearTimeout(initialTimer);
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkConnection]);

  // Label für Anzeige
  const label = status === 'connected'
    ? `Verbunden${latency ? ` (${latency}ms)` : ''}`
    : status === 'unstable'
      ? `Instabil${latency ? ` (${latency}ms)` : ''}`
      : 'Keine Verbindung';

  return { status, latency, label };
}
